import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AccMaterialsModule } from "src/app/accountancy/utilities/modules/acc-materials/acc-materials.module";
import { DisbursementVoucherService, VoucherInterface, VoucherItemAddInterface } from './disbursement-voucher-service/disbursement-voucher.service';
import { PromptMessage } from 'src/app/accountancy/utilities/classes/promp-message.class';
import { Helper } from 'src/app/accountancy/utilities/classes/helper.class';
import { BehaviorSubject, finalize, map, of, Subscription, switchMap, tap } from 'rxjs';
import { WaitHandler } from 'src/app/accountancy/utilities/classes/wait-handler.class';
import { AccountingApiService } from 'src/app/accountancy/utilities/services/accounting-api/accounting-api.service';
import { AccChartOfAccountCategoryInterface, AccChartOfAccountInterface } from 'src/app/accountancy/utilities/interfaces/global.interface';
import { AccConfirmationService } from 'src/app/accountancy/custom-components/acc-confirmation/acc-confirmation-service/acc-confirmation.service';
import { ReimbursementItemInterface } from 'src/app/accountancy/utilities/interfaces/reimbursement.interface';

@Component({
  standalone: false,
  selector: 'app-disbursement-voucher',
  templateUrl: './disbursement-voucher.component.html',
  styleUrls: ['./disbursement-voucher.component.scss'],
})
export class DisbursementVoucherComponent implements OnInit, OnDestroy {

  public readonly Helper = Helper;
  public readonly waitHandler = new WaitHandler();
  public readonly prompt = new PromptMessage();

  public readonly fg = this._newFg();

  public readonly chartAccCategs$ = new BehaviorSubject<AccChartOfAccountCategoryInterface[]>([]);
  private readonly _getChartAccCategsSubs: Subscription = this._getChartAccCategs();

  public readonly chartAccList$ = new BehaviorSubject<string[]>([]);
  private readonly _getChartAccListSubs: Subscription = this._getChartAccList();

  constructor(
    @Inject(MAT_DIALOG_DATA) public passedData: DisbursementVoucherPassedData,

    private _accApi: AccountingApiService,
    private _disVS: DisbursementVoucherService,
    private _ref: MatDialogRef<DisbursementVoucherComponent>,
    private _confirm: AccConfirmationService,
  ) { }

  public ngOnInit(): void {
    this.fg.patchValue({
      Type: this.passedData.Type,
      CntrlNumber: this.passedData.CntrlNumber,
    }, { emitEvent: false });

    if (this.passedData.Type == "Reimbursement") {
      const items = this.passedData.ReimItems;
      if (items) {
        items.forEach(item => this.newVoucherItem({
          AccountCode: item.Account,
          Description: item.Description,
          SubAccount: null,
          Debit: null,
          Credit: null,
        }))
      }
    }
  }

  public ngOnDestroy(): void {
    this._getChartAccCategsSubs.unsubscribe();
    this._getChartAccListSubs.unsubscribe();
  }

  public onSubmit() {
    const formData = this.fg.value as Required<VoucherInterface>;

    if (Helper.getInvalidControls(this.fg, true).length) {
      this.prompt.set(Helper.errorMessage.requiredFields, "danger");
      return;
    }

    if (!formData.VoucherItems.length && this.passedData.Type === 'Cash Advance') {
      this.prompt.set("At least 1 item is required.", "danger");
      return;
    }

    const body = {
      ...formData,
      VoucherItems: undefined,
    };

    const selectedType = {
      "Cash Advance": "CA",
      "Reimbursement": "R",
      "Liquidation": "L",
    };

    const Items = formData.VoucherItems.map(item => ({
      ...item,
      Account: (item.AccountCode).split(") ")?.[1] ?? '',
      Code: Helper.extractNumbersInParentheses(item.AccountCode) ?? -1,
      ReferenceId: this.passedData.CntrlNumber,
      Type: selectedType[this.passedData.Type],
      AccountCode: undefined,
    } as VoucherItemAddInterface));

    this.prompt.clear();
    const onSubmitSubs: Subscription = of(null).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(() => this._disVS.generateVoucher(body).pipe(
        finalize(() => {
          this.waitHandler.finishRequest();
        })
      )),

      tap(() => this.waitHandler.newRequest()),
      switchMap(() => this._disVS.addVoucherItem(Items).pipe(
        finalize(() => {
          if (this.passedData.onSuccessGenerateVoucher) this.passedData.onSuccessGenerateVoucher();
          this.waitHandler.finishRequest();
          this._ref.close();
          onSubmitSubs.unsubscribe();
        })
      ))
    ).subscribe();
  }

  public newVoucherItem(value?: DisbursementFormValueInterface) {
    this.fg.controls.VoucherItems.push(this._newFgVoucherItem(value));
  }

  public removeVoucherItem(index: number) {
    this._confirm.open({
      title: "Delete Item",
      message: `Are you sure you want to delete Item #${index + 1}`,
      onAnswer: (yes, matDialogRef, data) => {
        if (yes) this.fg.controls.VoucherItems.removeAt(index);
        matDialogRef.close();
      },
    });
  }

  private _newFg() {
    return new FormGroup({
      Type: new FormControl<"Reimbursement" | "Cash Advance" | "Liquidation" | null | undefined>(undefined, { validators: [Validators.required] }),
      CntrlNumber: new FormControl<string | null | undefined>(undefined, { validators: [Validators.required] }),
      PayToTheOrderOf: new FormControl<string | null | undefined>(undefined, { validators: [Validators.required] }),
      Payee: new FormControl<string | null | undefined>(undefined, { validators: [Validators.required] }),
      Amount: new FormControl<number | null | undefined>(this.passedData.ApprovedAmount, { validators: [Validators.required] }),
      Particulars: new FormControl<string | null | undefined>(undefined, { validators: [Validators.required] }),
      VATReg: new FormControl<number | null | undefined>(0, { validators: [Validators.required] }),
      VoucherItems: new FormArray<FormGroup<{
        AccountCode: FormControl<string | undefined | null>;
        Description: FormControl<string | undefined | null>;
        SubAccount: FormControl<string | undefined | null>;
        Debit: FormControl<number | undefined | null>;
        Credit: FormControl<number | undefined | null>;
      }>>([], { validators: [Validators.required] }),
    });
  }

  private _newFgVoucherItem(value?: DisbursementFormValueInterface) {
    const fcDebit = new FormControl<number | undefined | null>(value?.Debit);
    if (this.passedData.Type != 'Cash Advance') fcDebit.addValidators([Validators.required]);

    return new FormGroup({
      AccountCode: new FormControl<string | undefined | null>(value?.AccountCode, { validators: [Validators.required] }),
      Description: new FormControl<string | undefined | null>(value?.Description, { validators: [Validators.required] }),
      SubAccount: new FormControl<string | undefined | null>(value?.SubAccount, { validators: [Validators.required] }),
      Debit: fcDebit,
      Credit: new FormControl<number | undefined | null>(value?.Credit, { validators: [Validators.required] }),
    });
  }

  private _getChartAccCategs() {
    return of(null).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(() => this._accApi.chartOfAccounts.category.getCategoryList(0, 9999).pipe(
        finalize(() => this.waitHandler.finishRequest()),
        map(values => values.body?.data),
        tap(values => this.chartAccCategs$.next(values ?? []))
      ))
    ).subscribe();
  }

  private _getChartAccList() {
    return of(null).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(() => this._accApi.chartOfAccounts.getChartOfAccountList(0, 9999).pipe(
        finalize(() => this.waitHandler.finishRequest()),
        map(values => values.body?.data),
        tap(values => this.chartAccList$.next(values?.map(v => `(${v.Code}) ${v.Category.Name} - ${v.Account}`) ?? []))
      ))
    ).subscribe();
  }
}

interface DisbursementFormValueInterface {
  AccountCode: string | undefined | null,
  Description: string | undefined | null,
  SubAccount: string | undefined | null,
  Debit: number | undefined | null,
  Credit: number | undefined | null,
}

export interface DisbursementVoucherPassedData {
  Type: "Reimbursement" | "Cash Advance" | "Liquidation";
  CntrlNumber: string;
  ApprovedAmount?: number,
  ReimItems?: ReimbursementItemInterface[],
  onSuccessGenerateVoucher?: () => void,
};